import React, { useEffect, useState } from 'react';
import truncate from 'lodash/truncate';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import { richText } from '../../../util/richText';
import { ensureUser, ensureCurrentUser } from '../../../util/data';
import { propTypes } from '../../../util/types';

import { AvatarLarge, NamedLink, InlineTextButton } from '../../../components';

import css from './UserCard.module.css';

const BIO_COLLAPSED_LENGTH = 170;
const MIN_LENGTH_FOR_LONG_WORDS = 20;

const truncated = s => {
  return truncate(s, {
    length: BIO_COLLAPSED_LENGTH,
    separator: /\s|,|\.|:|;/,
    omission: '…',
  });
};

const ExpandableBio = props => {
  const [expand, setExpand] = useState(false);
  const { className, bio } = props;
  const bioWithLinks = richText(bio, {
    linkify: true,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
  });
  const truncatedBio = richText(truncated(bio), {
    linkify: true,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
    breakChars: '/',
  });

  const handleShowMoreClick = () => {
    setExpand(true);
  };
  const showMore = (
    <InlineTextButton rootClassName={css.showMore} onClick={handleShowMoreClick}>
      <FormattedMessage id="UserCard.showFullBioLink" />
    </InlineTextButton>
  );
  return (
    <p className={className}>
      {expand ? bioWithLinks : truncatedBio}
      {bio.length >= BIO_COLLAPSED_LENGTH && !expand ? showMore : null}
    </p>
  );
};

const UserCard = props => {
  const [mounted, setMounted] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const { rootClassName, className, user, currentUser, onContactUser, showContact = true, email, phoneNumber, listingTitle } = props;

  const userIsCurrentUser = user && user.type === 'currentUser';
  const ensuredUser = userIsCurrentUser ? ensureCurrentUser(user) : ensureUser(user);

  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const isCurrentUser =
    ensuredUser.id && ensuredCurrentUser.id && ensuredUser.id.uuid === ensuredCurrentUser.id.uuid;
  const { displayName, bio } = ensuredUser.attributes.profile;

  const handleContactUserClick = () => {
    onContactUser(user);
  };

  const hasBio = !!bio;
  const classes = classNames(rootClassName || css.root, className);
  const linkClasses = classNames(css.links, {
    [css.withBioMissingAbove]: !hasBio,
  });

  const separator =
    (mounted && isCurrentUser) || !showContact ? null : (
      <span className={css.linkSeparator}>•</span>
    );

  const contact = showContact ? (
    <InlineTextButton
      rootClassName={css.contact}
      onClick={handleContactUserClick}
      enforcePagePreloadFor="SignupPage"
    >
      <FormattedMessage id="UserCard.contactUser" />
    </InlineTextButton>
  ) : null;

  const editProfileMobile = (
    <span className={css.editProfileMobile}>
      <span className={css.linkSeparator}>•</span>
      <NamedLink name="ProfileSettingsPage">
        <FormattedMessage id="ListingPage.editProfileLink" />
      </NamedLink>
    </span>
  );

  const editProfileDesktop =
    mounted && isCurrentUser ? (
      <NamedLink className={css.editProfileDesktop} name="ProfileSettingsPage">
        <FormattedMessage id="ListingPage.editProfileLink" />
      </NamedLink>
    ) : null;

  const mailtoSubject = encodeURIComponent(
    `[Cobuco] Demande à propos de : ${listingTitle || 'votre annonce'}`
  );
  const mailtoBody = encodeURIComponent(
    `Bonjour,\n\nJe vous contacte via Cobuco à propos de votre annonce "${listingTitle || ''}".\n\n`
  );

  const contactInfo = (email || phoneNumber) ? (
    <div className={css.contactInfo}>
      {email && (
        <div className={css.contactItem}>
          <span className={css.contactLabel}>Email : </span>
          <a
            href={`mailto:${email}?subject=${mailtoSubject}&body=${mailtoBody}`}
            className={css.contactLink}
          >
            {email}
          </a>
        </div>
      )}
      {phoneNumber && (
        <div className={css.contactItem}>
          <span className={css.contactLabel}>Téléphone : </span>
          <a
            href="#"
            className={css.contactLink}
            onClick={e => {
              e.preventDefault();
              setShowPhoneModal(true);
            }}
          >
            {phoneNumber}
          </a>
        </div>
      )}
      {showPhoneModal && (
        <div className={css.phoneModalOverlay} onClick={() => setShowPhoneModal(false)}>
          <div className={css.phoneModal} onClick={e => e.stopPropagation()}>
            <p className={css.phoneModalTitle}>Avant d'appeler</p>
            <p className={css.phoneModalText}>
              Pensez à mentionner que vous appelez depuis <strong>Cobuco</strong> à propos de
              l'annonce <strong>"{listingTitle || ''}"</strong>.
            </p>
            <a href={`tel:${phoneNumber}`} className={css.phoneModalButton}>
              Appeler le {phoneNumber}
            </a>
            <button
              type="button"
              className={css.phoneModalClose}
              onClick={() => setShowPhoneModal(false)}
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  ) : null;

  const links = ensuredUser.id ? (
    <p className={linkClasses}>
      <NamedLink className={css.link} name="ProfilePage" params={{ id: ensuredUser.id.uuid }}>
        <FormattedMessage id="UserCard.viewProfileLink" />
      </NamedLink>
      {separator}
      {mounted && isCurrentUser ? editProfileMobile : contact}
    </p>
  ) : null;

  return (
    <div className={classes}>
      <div className={css.content}>
        <AvatarLarge className={css.avatar} user={user} />
        <div className={css.info}>
          <div className={css.headingRow}>
            <FormattedMessage id="UserCard.heading" values={{ name: displayName }} />
            {editProfileDesktop}
          </div>
          {hasBio ? <ExpandableBio className={css.desktopBio} bio={bio} /> : null}
          {contactInfo}
          {links}
        </div>
      </div>
      {hasBio ? <ExpandableBio className={css.mobileBio} bio={bio} /> : null}
    </div>
  );
};

export default UserCard;
